import {Severity} from 'platform/foundation';
import {ZodSchema} from 'zod';

import {useCallback, useEffect, useMemo, useState} from 'react';

import {isNilOrEmpty} from 'ramda-adjunct';

import {assignSafely, getByPathSafely, useDebouncedCallback} from 'shared/web';

import {BaseVehicleValidationSchema} from '../../core/schema/BaseVehicleValidationSchema';
import {BaseVehicle} from '../../core/types/BaseVehicle';
import {FieldRegistry} from '../FieldRegistry';
import {getRequiredZodFieldPaths} from '../utils/getRequiredZodFieldPaths';
import {useVehicleFormContext} from './useVehicleFormContext';

type StepIndicator = {severity: Severity | undefined; required: number; missing: number};

interface UseWizardIndicatorsProps {
  stepIds: readonly string[];
  schema?: ZodSchema;
}

const getIndicators = ({
  stepIds,
  fieldRegistry,
  requiredSet,
  values,
}: {
  stepIds: readonly string[];
  fieldRegistry: FieldRegistry;
  requiredSet: Set<string>;
  values: Partial<BaseVehicle>;
}) => {
  const map: Record<string, StepIndicator> = {};
  for (const id of stepIds) {
    const fields = [...fieldRegistry.getFields(id)].filter((name) => requiredSet.has(name));
    const required = fields.length;
    let missing = 0;
    for (const field of fields) {
      const value = getByPathSafely(values, field as keyof BaseVehicle);
      if (isNilOrEmpty(value)) {
        missing++;
      }
    }
    let severity: Severity | undefined;
    if (missing > 0) {
      severity = 'warning';
    } else if (required > 0 && missing === 0) {
      severity = 'success';
    } else {
      severity = undefined;
    }
    assignSafely(map, id, {severity, required, missing});
  }
  return map;
};

export function useWizardIndicators({
  stepIds,
  schema = BaseVehicleValidationSchema,
}: UseWizardIndicatorsProps) {
  const [indicators, setIndicators] = useState<Record<string, StepIndicator>>({});
  const {formApi, fieldRegistry, mandatoryFields} = useVehicleFormContext<BaseVehicle>();

  const schemaRequiredFields = useMemo(() => getRequiredZodFieldPaths(schema), [schema]);
  const requiredSet = useMemo(
    () => new Set<string>([...schemaRequiredFields, ...mandatoryFields]),
    [schemaRequiredFields, mandatoryFields]
  );

  // Stable callback that doesn't cause infinite loops
  const stableRecalculate = useCallback(
    (values: Partial<BaseVehicle>) => {
      setIndicators(getIndicators({stepIds, fieldRegistry, requiredSet, values}));
    },
    [stepIds, fieldRegistry, requiredSet]
  );

  const recalculateIndicators = useDebouncedCallback(stableRecalculate, 300);

  useEffect(
    function subscribeToIndicatorChanges() {
      const values = formApi.getValues();
      // Calculate initial indicators
      stableRecalculate(values); // eslint-disable-line react/set-state-in-effect -- Pre-existing violation

      let unsubscribeForm: (() => void) | undefined;

      // Function to update form subscription when registry changes
      const updateFormSubscription = () => {
        // Clean up old subscription
        unsubscribeForm?.();

        // Get current field names
        const fieldNames = stepIds.flatMap((id) => Array.from(fieldRegistry.getFields(id)));

        if (fieldNames.length > 0) {
          unsubscribeForm = formApi.subscribe({
            name: fieldNames as (keyof BaseVehicle)[],
            callback: (state) => {
              recalculateIndicators(state.values);
            },
          });
        }
      };

      // Subscribe to registry changes and update form subscription
      const unsubscribeRegistry = fieldRegistry.subscribe(() => {
        const currentValues = formApi.getValues();
        stableRecalculate(currentValues);
        updateFormSubscription(); // Re-subscribe with new field list
      });

      // Initial subscription setup
      updateFormSubscription();

      return () => {
        unsubscribeRegistry();
        unsubscribeForm?.();
      };
    },
    [formApi, fieldRegistry, stepIds, requiredSet, stableRecalculate, recalculateIndicators]
  );

  return indicators;
}
